import React, { useState } from 'react';
import { Box, Drawer, Card, CardContent, CardHeader, Typography, CircularProgress } from '@mui/material';
import api from "../../../../services/api";
import FormAddEditToolbar from "../../../../components/global/AddEditBar";
import { useNavigate } from 'react-router-dom';
import { useNotifier } from "muinotify";
import FieldsForm from "../../FieldsForm";
import useSWR, { mutate } from 'swr';
import { useParams } from "react-router";
import ErrorNotification from '../../../../components/ErrorNotification/ErrorNotification';
import * as yup from 'yup';
import ProductionCategoriesForm from '../ProductionCategoriesForm';

const validationSchema = yup.object().shape({
  p_id: yup
    .number()
    .required('Production is required'),

  pc_descr: yup
    .string()
    .required('Description is required')
    .min(1, 'Description must be a string'),

  pc_notes: yup
    .string()
    .nullable(true),

  p_aa: yup
    .number()
    .positive('A/A must be a positive number')
    .integer('A/A must be an integer')
    .nullable(true),

  pc_type_id: yup
    .number()
    .required('Type is required'),
});

const headerNames = {
  p_id: 'Παραγωγή',
  pc_descr: 'Περιγραφή',
  pc_notes: 'Σημειώσεις',
  p_aa: 'A/A',
  pc_type_id: 'Είδος'
};

const URL = `production-category`;

const ProductionCategoriesEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: productions_ed } = useSWR(`/productions`);
  const { data: pc_type } = useSWR(`/production-category-type`);
  const { notifier } = useNotifier();

  const [state, setState] = useState(null);
  const [errorMessage, setErrorMessage] = useState();
  const [errors, setErrors] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);

  const clientKey = `/${URL}/${id}`;

  if (!state) {
    mutate(clientKey);
  }

  const { data: productions } = useSWR(clientKey, {
    revalidateOnFocus: false,
    revalidateOnMount: true,
    onSuccess: (data) => {
      if (data) {
        setState(data);
        setDrawerOpen(true);
        setIsUpdating(false);
      }
    },
    onError: (err) => {
      setErrorMessage(err);
    }
  });

  function updateField(fieldName, value) {
    setState({ ...state, [fieldName]: value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await validationSchema.validate(state, { abortEarly: false });
      setErrors({});
      setIsUpdating(true);


      api.patch(`/${URL}/${state.id}`, state)
        .then(response => {
          mutate(response);
          notifier("Record updated.", { variant: "success" });
          setDrawerOpen(false);
          navigate(-1);
        })
        .catch(error => {
          setErrorMessage(error);
          notifier("Error: " + error, { variant: "error" });
        })
        .finally(() => {
          setIsUpdating(false);
        });

    } catch (validationErrors) {
      const formattedErrors = {};
      validationErrors.inner.forEach((error) => {
        formattedErrors[error.path] = error.message;
      });
      setErrors(formattedErrors);
    }
  };

  const handleSave = () => {
    const form = document.getElementById(`${URL}-form`);
    if (form) {
      form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const [drawerOpen, setDrawerOpen] = useState(true);

  return (
    <>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCancel}
        PaperProps={{
          style: {
            width: '100%',
            height: '100vh',
            maxWidth: '800px',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Card
          sx={{
            width: '100%',
            maxWidth: 800,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden', // Prevent overflow from Card itself
          }}
        >
          {/* Header Section */}
          <Box>
            <CardHeader
              title={<Typography variant="h3" gutterBottom>Επεξεργασία Κατηγορίας Παραγωγής</Typography>}
            />
          </Box>

          {/* Scrollable Content Section */}
          <CardContent
            sx={{
              display: 'flex',
              height: '100vh',
              flexGrow: 1, // Allow this section to grow and shrink
              overflowY: 'auto',
              margin: 'auto',
            }}
          >
            <form id={`${URL}-form`} onSubmit={handleSubmit}>
              <ProductionCategoriesForm
                state={state}
                errors={errors}
                updateField={updateField}
                headerNames={headerNames}
                productions={productions_ed}
                pc_type={pc_type}
              />
            </form>
            {isUpdating && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  zIndex: 1000,
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </CardContent>

          {/* Toolbar (Footer Section) */}
          <Box>
            <FormAddEditToolbar
              handleSave={handleSave}
              handleCancel={handleCancel}

            />
          </Box>
        </Card>
      </Drawer>

    </>
  );
};

export default ProductionCategoriesEdit;
